/**
 * Cover Art — pixel-faithful port of `design/Cover Art.dc.html`
 * (DC-RUNTIME §6.2).
 *
 * Contract notes for the screen lanes:
 *  - This component has NO intrinsic size. The inner stack is
 *    `width:100%;height:100%`; every one of the 15 prototype call sites sized it
 *    through the host wrapper with `style="position:absolute;inset:0"` inside a
 *    `position:relative` box that carries the aspect ratio and the fallback
 *    gradient. Port that box, not a size prop:
 *
 *      <div style={{ position:'relative', aspectRatio:'2/3', background: t.fall, overflow:'hidden' }}>
 *        <CoverArt style={{ position:'absolute', inset:0 }} fs={15} ini={t.ini} hasCover={…} cover={…} />
 *      </div>
 *
 *  - `hasCover` / `cover` are NOT in the selectors: `selectors.ts` deliberately
 *    returns a *candidate ladder* (`coverCands` / `hdrCands` from
 *    `lib/logic.ts#artCands`) instead of a resolved background, because
 *    resolution is an `Image()` probe and therefore a component concern. That
 *    probe is `components/useArt.ts`:
 *
 *      const art = useArt(g.id + 'p', g.coverCands);   // 'w' + hdrCands for
 *      …hasCover={art.hasCover} cover={art.cover}      // 460×215 thumbnails
 *
 *    While it is still walking the ladder — and if every rung 404s —
 *    `hasCover` is false and the checker + initials render, which is the
 *    correct loading / no-art state.
 */

import type { CSSProperties } from 'react';

export interface CoverArtProps {
  /** initials px — DC default 15 */
  fs?: number | string;
  /** 1–2 letter fallback, from `tileVM().ini` */
  ini?: string;
  hasCover?: boolean;
  /** CSS background shorthand for the resolved art */
  cover?: string;
  /** initials opacity — DC default 0.25 */
  alpha?: number | string;
  /** host-wrapper style — positional properties only */
  style?: CSSProperties;
}

export function CoverArt({ fs, ini, hasCover, cover, alpha, style }: CoverArtProps) {
  return (
    <div className="sc-host" data-sc-name="Cover Art" style={style}>
      <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gt-checker)' }} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: (fs ?? 15) + 'px',
            color: 'rgba(255,255,255,' + (alpha ?? 0.25) + ')',
          }}
        >
          <span>{ini ?? ''}</span>
        </div>
        {hasCover ? <div style={{ position: 'absolute', inset: 0, background: cover }} /> : null}
      </div>
    </div>
  );
}
